import { useEffect, useState } from 'react';
import { Segmented } from 'antd';
import FilterDropdown from '../FilterDropdown/FilterDropdown';
import { t } from '../../i18n/i18n';
import '../SegmentedTabs/SegmentedTabs.scss';
import './Pagination.scss';

const WINDOW = 5;

/**
 * Windowed page numbers. The design shows a flat `1 2 3` run with no ellipsis,
 * so long ranges slide the window instead of collapsing it — and once the run
 * can no longer reach every page, the jump field below earns its place.
 */
export function pageWindow(current, total) {
  if (total <= WINDOW) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.min(Math.max(current - Math.floor(WINDOW / 2), 1), total - WINDOW + 1);
  return Array.from({ length: WINDOW }, (_, i) => start + i);
}

/**
 * Rows per page.
 *
 * The app's own dropdown rather than a `<select>`: a native one draws the
 * platform's popup, which cannot be styled to match and renders light on a
 * dark page on Windows. `FilterDropdown` is already the table toolbar's filter
 * control, so the two read as the same kind of thing — and its trigger is the
 * `toolbar` button, whose 32px/#161616/11.2px geometry is the pager track's.
 *
 * Exported because the leaderboard builds its own footer rather than using the
 * pager below, and the control should not exist twice.
 */
export function PageSize({ value, options, onChange, className = '' }) {
  return (
    <div className={`table-pagination__size ${className}`.trim()}>
      <span className="table-pagination__size-label">{t('Rows')}</span>
      <FilterDropdown
        className="table-pagination__size-drop"
        options={options.map((size) => ({ label: String(size), value: size }))}
        value={value}
        onChange={onChange}
        // The fallback never shows — a size is always one of the options — but
        // without it an unmatched value would read "Filters".
        label={String(value)}
        // The pager is the last row of its card, so a menu dropping downward
        // would open past the card's own edge.
        placement="up"
        align="left"
        icon="chevron-down"
      />
    </div>
  );
}

/**
 * Type a page and go straight to it.
 *
 * Shown wherever the pager is. It was once held back until the sliding window
 * could no longer reach every page, on the grounds that five pages are all one
 * click away — but the page size is the reader's to choose, and 218 rows at 50
 * is exactly five pages, so the field kept disappearing as a side effect of
 * changing the size. A control that comes and goes on its own is worse than
 * one that is occasionally redundant.
 *
 * Kept as its own input rather than driven off the parent's `page`: a half-typed
 * "1" on the way to "12" must not navigate, so it commits on Enter or blur and
 * resets to the real page if what was typed is not one.
 */
function GoToPage({ page, pageCount, onChange }) {
  const [draft, setDraft] = useState(String(page));
  /** A rejected entry snaps back, which on its own looks like nothing happened. */
  const [rejected, setRejected] = useState(false);

  // Follow the pager when the page changes from anywhere else — a number
  // button, the arrows, or a filter resetting to page 1.
  useEffect(() => setDraft(String(page)), [page]);

  const commit = () => {
    const next = Number(draft);
    if (!Number.isInteger(next) || next < 1 || next > pageCount) {
      setDraft(String(page));
      setRejected(true);
      return;
    }
    if (next !== page) onChange(next);
  };

  return (
    <label className="table-pagination__goto">
      <span className="table-pagination__goto-label">{t('Go to')}</span>

      {/* Field and total share one pill so the pair reads as a single control
          — "3 / 22" — rather than an input with a stray number beside it. */}
      <span
        className={`table-pagination__goto-field${rejected ? ' is-rejected' : ''}`}
        onAnimationEnd={() => setRejected(false)}
      >
        <input
          className="table-pagination__goto-input"
          // `inputMode` rather than `type="number"`: the spinner buttons are the
          // browser's own geometry and the Style Guide draws no field with one.
          inputMode="numeric"
          value={draft}
          aria-label={t('Go to page, 1 to {{count}}', { count: pageCount })}
          // Wide enough for the longest page number this table can hold, and no
          // wider. Two digits is the floor: a single-digit field is a sliver
          // that does not read as something you can type into.
          style={{ width: `${Math.max(String(pageCount).length, 2)}ch` }}
          onChange={(e) => {
            setRejected(false);
            setDraft(e.target.value.replace(/[^0-9]/g, ''));
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') setDraft(String(page));
          }}
        />
        <span className="table-pagination__goto-of">/ {pageCount}</span>
      </span>
    </label>
  );
}

/**
 * `pageSizeOptions` is opt-in, and deliberately so. In client mode the table
 * slices its own rows and any size works; in server mode the page size has to
 * reach the request, which only happens if the caller reads the second argument
 * of `onPageChange`. Callers that ignore it would get a control that appears to
 * work and changes nothing, so a table shows the selector only once its owner
 * has said it can honour it.
 */
export default function Pagination({
  page,
  pageCount,
  from,
  to,
  total,
  onChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}) {
  if (total === 0) return null;

  const step = (next) => () => onChange(Math.min(Math.max(next, 1), pageCount));
  const showSizes = Boolean(pageSizeOptions?.length && onPageSizeChange);

  return (
    <div className="table-pagination">
      <div className="table-pagination__left">
        <span className="table-pagination__summary">
          {t('Showing {{from}}–{{to}} of {{total}}', { from, to, total })}
        </span>

        {showSizes && (
          <PageSize value={pageSize} options={pageSizeOptions} onChange={onPageSizeChange} />
        )}
      </div>

      {pageCount > 1 && (
        <div className="table-pagination__right">
          <div className="table-pagination__pager">
            <button
              type="button"
              className="table-pagination__page table-pagination__page--nav"
              onClick={step(page - 1)}
              disabled={page === 1}
              aria-label={t('Previous page')}
            >
              ‹
            </button>

            <Segmented
              className="ac-seg table-pagination__seg"
              options={pageWindow(page, pageCount)}
              value={page}
              onChange={onChange}
            />

            <button
              type="button"
              className="table-pagination__page table-pagination__page--nav"
              onClick={step(page + 1)}
              disabled={page === pageCount}
              aria-label={t('Next page')}
            >
              ›
            </button>
          </div>

          <GoToPage page={page} pageCount={pageCount} onChange={onChange} />
        </div>
      )}
    </div>
  );
}
